import React from 'react';
import { Cookie, Settings, EyeOff, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CookiesPolicy: React.FC = () => {
  const { i18n } = useTranslation();
  const isEng = i18n.language.startsWith('en');

  const content = {
    title: isEng ? "Cookie Policy" : "Política de Cookies",
    subtitle: isEng
      ? "How we use cookies to improve your experience."
      : "Transparência total sobre como usamos pequenos ficheiros para melhorar a sua experiência no portal MotoRush.",
    sections: [
      {
        icon: <CheckCircle className="text-green-500 w-6 h-6" />,
        title: isEng ? "Essential Cookies" : "Cookies Essenciais (Obrigatórios)",
        desc: isEng
          ? "Used to keep administrators or corporate clients logged into the web portal."
          : "Estes são fundamentais para o funcionamento do site. Usamos estes cookies especificamente para manter a sessão dos administradores ou dos clientes corporativos (B2B) iniciada de forma segura enquanto navegam pelo portal web. Sem estes, certas funcionalidades não operam."
      },
      {
        icon: <EyeOff className="text-purple-500 w-6 h-6" />,
        title: isEng ? "Analytical Cookies" : "Cookies Analíticos (Estatísticas)",
        desc: isEng
          ? "We use external tools to count site visitors and understand usage."
          : "Utilizamos ferramentas externas (como Google Analytics) de forma anonimizada para contar quantos visitantes acedem ao nosso site, de onde vêm e que páginas visitam. Isto ajuda-nos a melhorar o nosso marketing e a optimizar a experiência da nossa Landing Page."
      },
      {
        icon: <Settings className="text-gray-500 w-6 h-6" />,
        title: isEng ? "How to Disable" : "Como Desativar nas Definições",
        desc: isEng
          ? "You can block cookies in your browser settings."
          : "Se não deseja que o nosso site armazene cookies no seu dispositivo, pode configurar o seu navegador de internet (Chrome, Safari, Firefox, Edge) para bloquear ou alertar quando cookies forem enviados. Note que ao bloquear cookies essenciais, o portal corporativo pode não funcionar correctamente."
      }
    ]
  };

  return (
    <div className="pt-24 pb-16 bg-gray-50 min-h-screen animate-fadeIn">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center p-3 bg-orange-100 rounded-2xl mb-4 text-[#FF6B35]">
            <Cookie size={32} />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {content.title}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {content.subtitle}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12 space-y-10">
          {content.sections.map((section, idx) => (
            <div key={idx} className="flex flex-col md:flex-row gap-6 items-start group border-b border-gray-50 pb-8 last:border-0 last:pb-0">
              <div className="p-4 bg-gray-50 rounded-xl group-hover:bg-orange-50 transition-colors shrink-0">
                {section.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{section.title}</h3>
                <p className="text-gray-600 leading-relaxed text-justify">
                  {section.desc}
                </p>
              </div>
            </div>
          ))}

          <div className="mt-8 p-6 bg-blue-50 rounded-2xl border border-blue-100">
            <h4 className="font-bold text-blue-900 mb-2">
              {isEng ? "Our Promise" : "O Nosso Compromisso"}
            </h4>
            <p className="text-sm text-blue-800">
              {isEng
                ? "We do not use tracking cookies for intrusive personalized advertising across other websites."
                : "A MotoRush não utiliza cookies de rastreio para fins de publicidade altamente intrusiva fora do nosso ecossistema. Respeitamos a sua privacidade e não vendemos os seus dados de navegação."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;
